import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/i18n/config";

const LANG_ARIA: Record<SupportedLanguage, string> = {
	en: "English",
	pt: "Português",
};

function FlagIcon({ lang }: { lang: SupportedLanguage }): ReactNode {
	if (lang === "pt") {
		return (
			<svg className="lang-switch__flag" viewBox="0 0 20 14" aria-hidden="true">
				<rect width="20" height="14" fill="#009b3a" />
				<path d="M10 1.2 18.2 7 10 12.8 1.8 7Z" fill="#fedf00" />
				<circle cx="10" cy="7" r="3.1" fill="#002776" />
				<path
					d="M10 5.1c.6 1.1 1.8 1.9 3.1 1.9-.5.4-1.1.7-1.8.8-.1 1.3-.8 2.4-1.8 3.1-.9-.7-1.6-1.8-1.8-3.1-.7-.1-1.3-.4-1.8-.8 1.3 0 2.5-.8 3.1-1.9Z"
					fill="#fff"
					opacity="0.95"
				/>
			</svg>
		);
	}

	return (
		<svg className="lang-switch__flag" viewBox="0 0 20 14" aria-hidden="true">
			<rect width="20" height="14" fill="#b22234" />
			<rect y="1.08" width="20" height="1.08" fill="#fff" />
			<rect y="3.23" width="20" height="1.08" fill="#fff" />
			<rect y="5.38" width="20" height="1.08" fill="#fff" />
			<rect y="7.54" width="20" height="1.08" fill="#fff" />
			<rect y="9.69" width="20" height="1.08" fill="#fff" />
			<rect y="11.85" width="20" height="1.08" fill="#fff" />
			<rect width="8.2" height="7.54" fill="#3c3b6e" />
			<circle cx="1.6" cy="1.4" r="0.35" fill="#fff" />
			<circle cx="3.2" cy="1.4" r="0.35" fill="#fff" />
			<circle cx="4.8" cy="1.4" r="0.35" fill="#fff" />
			<circle cx="6.4" cy="1.4" r="0.35" fill="#fff" />
			<circle cx="2.4" cy="2.5" r="0.35" fill="#fff" />
			<circle cx="4" cy="2.5" r="0.35" fill="#fff" />
			<circle cx="5.6" cy="2.5" r="0.35" fill="#fff" />
			<circle cx="1.6" cy="3.6" r="0.35" fill="#fff" />
			<circle cx="3.2" cy="3.6" r="0.35" fill="#fff" />
			<circle cx="4.8" cy="3.6" r="0.35" fill="#fff" />
			<circle cx="6.4" cy="3.6" r="0.35" fill="#fff" />
			<circle cx="2.4" cy="4.7" r="0.35" fill="#fff" />
			<circle cx="4" cy="4.7" r="0.35" fill="#fff" />
			<circle cx="5.6" cy="4.7" r="0.35" fill="#fff" />
			<circle cx="1.6" cy="5.8" r="0.35" fill="#fff" />
			<circle cx="3.2" cy="5.8" r="0.35" fill="#fff" />
			<circle cx="4.8" cy="5.8" r="0.35" fill="#fff" />
			<circle cx="6.4" cy="5.8" r="0.35" fill="#fff" />
		</svg>
	);
}

export function LanguageSwitcher(): ReactNode {
	const { i18n } = useTranslation();
	const current = (i18n.resolvedLanguage ?? i18n.language ?? "en") as SupportedLanguage;

	const onPick = (lng: SupportedLanguage): void => {
		if (lng === current) return;
		void i18n.changeLanguage(lng);
	};

	return (
		<fieldset className="lang-switch" aria-label="Language">
			{SUPPORTED_LANGUAGES.map((lng) => (
				<button
					key={lng}
					type="button"
					className={`lang-switch__btn${current === lng ? " is-active" : ""}`}
					aria-label={LANG_ARIA[lng]}
					aria-pressed={current === lng}
					onClick={() => onPick(lng)}
				>
					<FlagIcon lang={lng} />
				</button>
			))}
		</fieldset>
	);
}
